import styled, { css } from 'styled-components';
import {
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  StylesConfig,
} from 'react-select';

import { theme } from '@/themes/main';

import { OptionType } from './SelectDefault';

type StyleProps = {
  $isActive?: boolean;
  $isDisabled?: boolean;
};

function customSelectStyles<T, V extends boolean>(): StylesConfig<T, V> {
  const {
    colors,
    typography,
  } = theme;

  return {
    control: (base: CSSObjectWithLabel, state) => ({
      ...base,
      borderColor: colors.neutral[300],
      borderRadius: 0,
      boxShadow: 'unset',
      color: colors.neutral[700],
      fontFamily: typography.fontFamily.regular,
      fontSize: '16px',
      fontWeight: 400,
      gap: '8px',
      height: '100%',
      lineHeight: '32px',
      padding: '12px 16px',
      width: '100%',
      zIndex: '1',

      // eslint-disable-next-line sort-keys
      '&:hover': {
        boxShadow: 'unset',
      },
      // eslint-disable-next-line sort-keys
      '&:active:': {
        borderColor: 'yellow',
      },
    }),
    // @ts-expect-error props mismatch
    dropdownIndicator: (base: CSSObjectWithLabel, state: DropdownIndicatorProps) => ({
      ...base,
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
      transition: 'all .2s ease',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      height: '100%',
      zIndex: 2,
    }),
    input: (base: CSSObjectWithLabel) => ({
      ...base,
      width: 0,
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      boxShadow: 'unset',
      marginTop: '10px',
      zIndex: 9999,
    }),
    menuList: base => ({
      ...base,
      background: colors.white,
      border: 0,
      color: colors.neutral[700],
      padding: '16px 8px',
    }),

    option: (base: CSSObjectWithLabel, state) => ({
      ...base,
      backgroundColor: colors.white,
      color: colors.neutral[700],
      fontSize: '16px',
      height: '40px',
      marginTop: '8px',
      svg: {
        display: state.isSelected ? 'initial' : 'none',
      },
      // eslint-disable-next-line sort-keys
      ':hover': {
        backgroundColor: colors.neutral[200],
      },
    }),
    placeholder: base => ({
      ...base,
    }),
    singleValue: base => ({
      ...base,
      height: '100%',
    }),
    valueContainer: base => ({
      ...base,
      padding: 0,
    }),
  };
}

export const customSingleSelectStyles = customSelectStyles<OptionType, false>();
export const customMultipleSelectStyles = customSelectStyles<
  Array<OptionType>,
  true
>();

const activeState = css`
  top: -6px;
  width: auto;
  padding: 0 2px;
`;

export const StyledContainer = styled.div<StyleProps>`
  position: relative;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.medium};

  &:focus-within label {
    ${activeState}
  }
`;

type OptionContainerProps = {
  $isDisabled?: boolean;
};

export const OptionContainer = styled.div<OptionContainerProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  color: inherit;
`;

